import React, { useEffect, useState } from "react";
import { Bell, BellOff, Mail, Smartphone, Loader2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import api, { formatApiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "../lib/push";
import { toast } from "sonner";

const CATS = ["sold", "wallet", "platform"];

export default function NotificationSettings() {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    api.get("/notifications/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    isPushEnabled().then(setPushOn);
  }, []);

  const toggle = async (channel, cat, val) => {
    const next = { ...prefs, [channel]: { ...prefs[channel], [cat]: val } };
    setPrefs(next);
    setSaving(true);
    try { await api.put("/notifications/preferences", { [channel]: next[channel] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePush = async (val) => {
    setPushBusy(true);
    try {
      if (val) { await enablePush(); setPushOn(true); toast.success(t("notifPrefs.pushEnabled")); }
      else { await disablePush(); setPushOn(false); toast.success(t("notifPrefs.pushDisabled")); }
    } catch (e) {
      if (e.message === "denied") toast.error(t("notifPrefs.pushDenied"));
      else toast.error(t("notifPrefs.pushError"));
    } finally { setPushBusy(false); }
  };

  if (!prefs) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 space-y-6" data-testid="notif-settings">
      <div>
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-[#008080]" />{t("notifPrefs.title")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("notifPrefs.subtitle")}</p>
      </div>

      {/* Browser push master switch */}
      <div className="flex items-start justify-between gap-4 rounded-2xl bg-teal-50/60 border border-teal-100 p-4">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 grid place-items-center rounded-full bg-white text-[#008080]">
            {pushOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800">{t("notifPrefs.browserPush")}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {supported ? t("notifPrefs.browserPushNote") : t("notifPrefs.pushUnsupported")}
            </p>
          </div>
        </div>
        {pushBusy ? <Loader2 className="h-5 w-5 animate-spin text-[#008080]" />
          : <Switch data-testid="push-master-switch" checked={pushOn} disabled={!supported} onCheckedChange={togglePush} />}
      </div>

      {/* Per-category grid */}
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 gap-y-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>{t("notifPrefs.event")}</span>
          <span className="flex items-center gap-1 justify-center w-14"><Mail className="h-3.5 w-3.5" />{t("notifPrefs.email")}</span>
          <span className="flex items-center gap-1 justify-center w-14"><Smartphone className="h-3.5 w-3.5" />{t("notifPrefs.push")}</span>
        </div>
        {CATS.map((cat) => (
          <div key={cat} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 rounded-2xl border border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-slate-800">{t(`notifPrefs.cat_${cat}`)}</p>
              <p className="text-xs text-slate-400">{t(`notifPrefs.cat_${cat}_desc`)}</p>
            </div>
            <div className="w-14 grid place-items-center">
              <Switch data-testid={`pref-email-${cat}`} checked={!!prefs.email[cat]} disabled={saving} onCheckedChange={(v) => toggle("email", cat, v)} />
            </div>
            <div className="w-14 grid place-items-center">
              <Switch data-testid={`pref-push-${cat}`} checked={!!prefs.push[cat]} disabled={saving} onCheckedChange={(v) => toggle("push", cat, v)} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">{t("notifPrefs.pushHint")}</p>
    </div>
  );
}
